import type { CareerIntel } from "@/lib/careerIntel";
import type { LearnerStart, RoadmapStage } from "@/lib/careerRoadmap";

/** Explicit assessment intent takes precedence over the earlier signup goal. */
export function isCareerSwitcher(start: LearnerStart | null | undefined): boolean {
  if (!start || (start.band && start.band !== "18plus")) return false;
  return start.careerIntent ? start.careerIntent === "switch" : start.primaryGoal === "switch_path";
}

/** Planning guidance only: no eligibility, scoring or inferred skill ownership. */
export function careerSwitcherRoadmap(intel?: CareerIntel | null): RoadmapStage[] {
  const target = intel?.name ?? "a new career direction";
  const requirements = intel?.mustHave.slice(0, 3) ?? [];
  return [
    {
      id: "switch-clarity", horizon: "Weeks 1–2 · Suggested pace", title: "Define the change you want",
      summary: "Keep what matters from your current work. Decide what you want to change.",
      actions: [
        "Write down what energises you, what you want less of, and the contribution you want to make.",
        "Set your minimum income, available learning hours, location needs and preferred working conditions.",
        intel ? `Explore ${target} alongside two alternatives. Use the reasons in your Career DNA to decide what to test, not as a fixed verdict.` : "Complete WCC360 to explore your Career DNA, then compare three career possibilities and the evidence behind each match.",
      ],
    },
    {
      id: "switch-evidence", horizon: "Weeks 2–3 · Suggested pace", title: "Map the experience you can carry forward",
      summary: "Your experience is a starting point. Transferability needs evidence, not assumptions.",
      actions: [
        "Collect three examples of work you delivered. Record the problem, your contribution and the outcome without sharing confidential material.",
        `Read five current job descriptions for ${target}. Separate skills you can demonstrate from skills you still need to develop.`,
        ...(requirements.length ? [`Compare your examples against these role requirements: ${requirements.join(", ")}. Mark each as evidenced, developing or not yet tested.`] : ["Ask someone in the field to review which of your examples are relevant to the work."]),
      ],
    },
    {
      id: "switch-test", horizon: "Weeks 3–5 · Suggested pace", title: "Try the work before committing",
      summary: "A small experiment can reveal more than another course purchase.",
      actions: [
        `Speak with two people doing ${target}. Ask about a typical week, entry routes and challenges for career switchers.`,
        ...(intel?.responsibilities.length ? [`Build a small, non-confidential sample around this responsibility: ${intel.responsibilities[0]}.`] : ["Try a short project, shadowing session or volunteer assignment that reflects the actual work."]),
        "Ask for feedback. Record what you enjoyed, what surprised you and whether you want to keep exploring this direction.",
      ],
    },
    {
      id: "switch-bridge", horizon: "Weeks 5–8 · Suggested pace", title: "Build a focused skills bridge",
      summary: "Develop the missing evidence without automatically starting your education again.",
      actions: [
        ...(requirements.length ? [`Choose one priority to develop from: ${requirements.join(", ")}.`] : ["Choose one skill gap from the job descriptions and practitioner feedback."]),
        "Compare a short course, supervised project and on-the-job route by total cost, weekly effort and the evidence each produces.",
        "Check country-specific qualification and licensing requirements with the official body. Prior experience does not automatically waive regulated requirements.",
        "Improve your work sample and ask a practitioner to review it before paying for further training.",
      ],
    },
    {
      id: "switch-position", horizon: "Weeks 8–10 · Suggested pace", title: "Position yourself for a bridge role",
      summary: "Show the connection between your past work and your new direction.",
      actions: [
        `Rewrite your résumé and LinkedIn summary around evidence relevant to ${target}, without claiming skills you have not demonstrated.`,
        "Compare internal transfers, adjacent roles and entry routes. Check the required field-specific experience rather than assuming your total years qualify you for a senior title.",
        "Prepare two project stories and one clear explanation of why you are changing direction. Practise them with a mentor or peer.",
      ],
    },
    {
      id: "switch-move", horizon: "Weeks 10–12 and beyond · Suggested pace", title: "Make an informed move and keep growing",
      summary: "These weeks organise exploration, not a promise of a job or a completed transition.",
      actions: [
        "Build a shortlist of suitable openings, request introductions and track applications and feedback.",
        "Before resigning, compare verified pay, benefits, learning support, notice periods and your financial runway. Choose the risk level that works for you.",
        ...(intel?.futureSkills.length ? [`Plan your first learning goals around: ${intel.futureSkills.slice(0, 3).join(", ")}.`] : ["Agree on practical learning goals and feedback check-ins for your first months in the new work."]),
        "Review your direction monthly. Extend the timeline or explore another route if the evidence changes.",
      ],
    },
  ];
}